import { makeStyles, Paper, TextField } from '@material-ui/core';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import VehicleService from '../../../services/VehicleService';
import DirectionService from '../../../services/DirectionService';
import CalendarVehicle from './CalendarVehicle';
import { AntTab, AntTabs } from '../../../components/ant-tab';

const useStyles = makeStyles((theme) => ({
  filterPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: '1px solid #dce6f3',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
  },
  tablePaper: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
    overflow: 'hidden',
    border: '1px solid #dce6f3',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
  },
  tabsContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(0.75),
    overflow: 'hidden',
    border: '1px solid #dce6f3',
    borderRadius: '10px',
    backgroundColor: '#f4f7fb',
    boxShadow: '0 3px 10px rgba(15, 23, 42, 0.06)',
  },
  tab: {
    width: '50%',
    minHeight: '46px !important',
    borderRadius: '8px !important',
    color: '#526581 !important',
    fontSize: '14px !important',
    fontWeight: '600 !important',
    textTransform: 'none !important',
    '&.Mui-selected': {
      backgroundColor: '#1976d2',
      color: '#ffffff !important',
      boxShadow: '0 4px 10px rgba(25, 118, 210, 0.24)',
    },
  },
}));

const getLocalDateValue = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().split('T')[0];
};

const formatDate = (value, fallback) => {
  const dateValue = value || fallback;
  if (!dateValue) return '—';

  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR');
};

const VehicleManagmentTable = () => {
  const classes = useStyles();
  const today = getLocalDateValue();
  const [filter, setFilter] = useState(() => ({
    vhc_id: '',
    begin: today,
    end: today,
    direction_id: localStorage.getItem('USER_DIR') || 0,
  }));
  const initialFilter = useRef(filter);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [voitures, setVoitures] = useState([]);
  const [rows, setRows] = useState([]);
  const [directions, setDirections] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const invalidDateRange = Boolean(filter.begin && filter.end && filter.begin > filter.end);

  const fetchAvailability = useCallback(async (currentFilter) => {
    if (!currentFilter?.begin || !currentFilter?.end || currentFilter.begin > currentFilter.end) {
      return;
    }

    setOpenLoadingSimpleBackdrop(true);
    setErrorMessage('');
    try {
      const result = await VehicleService.disponibilityVehicle(currentFilter);
      if (!result?.status) {
        setRows([]);
        setErrorMessage(result?.message || 'Impossible de récupérer la disponibilité des voitures.');
        return;
      }

      const data = Array.isArray(result.data) ? result.data : Object.values(result.data || {});
      setRows(data.map((row) => ({ ...row, id: row.vhc_id })));
    } catch (error) {
      console.error('Erreur lors du chargement de la disponibilité des voitures :', error);
      setRows([]);
      setErrorMessage('Une erreur est survenue lors du chargement de la disponibilité.');
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  }, []);

  useEffect(() => {
    const loadDirections = async () => {
      try {
        const result = await DirectionService.getDirection();
        setDirections(Array.isArray(result?.data) ? result.data : []);
      } catch (error) {
        console.error('Erreur lors du chargement des directions :', error);
      }
    };

    loadDirections();
    fetchAvailability(initialFilter.current);
  }, [fetchAvailability]);

  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const result = await VehicleService.vehicleByDirection(filter.direction_id || 0);
        setVoitures(Array.isArray(result?.data) ? result.data : []);
      } catch (error) {
        console.error('Erreur lors du chargement des voitures :', error);
        setVoitures([]);
      }
    };

    loadVehicles();
  }, [filter.direction_id]);

  const columns = useMemo(() => [
    {
      field: 'direction',
      headerName: 'Direction',
      flex: 0.65,
      minWidth: 130,
      renderCell: (params) => <Typography sx={{ fontWeight: 600 }}>{params.value || '—'}</Typography>,
    },
    {
      field: 'date_begin',
      headerName: 'Date début',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatDate(params.value, filter.begin),
    },
    {
      field: 'date_end',
      headerName: 'Date fin',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatDate(params.value, filter.end),
    },
    {
      field: 'vhc_imm',
      headerName: 'Voiture',
      flex: 0.75,
      minWidth: 150,
      renderCell: (params) => <Typography sx={{ fontWeight: 700, color: '#29476f' }}>{params.value || '—'}</Typography>,
    },
    {
      field: 'status_disponibility',
      headerName: 'Disponibilité',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isAvailable = params.value === 'Oui';
        return (
          <Chip
            label={isAvailable ? 'Disponible' : 'Non disponible'}
            color={isAvailable ? 'success' : 'error'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: 'observation',
      headerName: 'Observation',
      flex: 1.5,
      minWidth: 320,
      sortable: false,
      renderCell: (params) => {
        const unavailable = params.row.status_disponibility === 'Non';
        const missionnaries = Number(params.row.missionnaries) || 0;
        const observation = unavailable
          ? `Cause d’indisponibilité : mission · ${missionnaries} personne${missionnaries > 1 ? 's' : ''} prévue${missionnaries > 1 ? 's' : ''}`
          : "Aucune affectation sur la période recherchée";

        return (
          <Typography sx={{ py: 1.25, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
            {observation}
          </Typography>
        );
      },
    },
  ], [filter.begin, filter.end]);

  const selectedDirection = useMemo(
    () => directions.find((direction) => Number(direction.direction_id) === Number(filter.direction_id)) || null,
    [directions, filter.direction_id]
  );
  const selectedVehicle = useMemo(
    () => voitures.find((vehicle) => Number(vehicle.vhc_id) === Number(filter.vhc_id)) || null,
    [voitures, filter.vhc_id]
  );

  const handleFilter = () => {
    if (!invalidDateRange) {
      fetchAvailability(filter);
    }
  };

  return (
    <Grid container>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

      <Grid item xs={12}>
        <Paper className={classes.filterPaper} variant="outlined">
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={directions}
                size="small"
                value={selectedDirection}
                isOptionEqualToValue={(option, value) => Number(option.direction_id) === Number(value.direction_id)}
                getOptionLabel={(option) => option ? `${option.name} (${option.code})` : ''}
                onChange={(_, value) => {
                  setFilter((prev) => ({
                    ...prev,
                    direction_id: value?.direction_id || 0,
                    vhc_id: '',
                  }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" label="Direction" variant="outlined" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  options={voitures}
                  size="small"
                  value={selectedVehicle}
                  isOptionEqualToValue={(option, value) => Number(option.vhc_id) === Number(value.vhc_id)}
                  getOptionLabel={(option) => option?.vhc_imm || ''}
                  onChange={(_, value) => setFilter((prev) => ({ ...prev, vhc_id: value?.vhc_id || '' }))}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" label="Immatriculation" variant="outlined" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                label="Date début"
                InputLabelProps={{ shrink: true }}
                value={filter.begin}
                onChange={(event) => setFilter((prev) => ({ ...prev, begin: event.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                label="Date fin"
                InputLabelProps={{ shrink: true }}
                value={filter.end}
                onChange={(event) => setFilter((prev) => ({ ...prev, end: event.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={handleFilter}
                disabled={invalidDateRange || openLoadingSimpleBackdrop}
                sx={{ minHeight: 40, fontWeight: 700 }}
              >
                Filtrer
              </Button>
            </Grid>
          </Grid>

          {invalidDateRange && (
            <Alert severity="error" sx={{ mt: 2 }}>
              La date de fin doit être supérieure ou égale à la date de début.
            </Alert>
          )}
          {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Box className={classes.tabsContainer}>
          <AntTabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            aria-label="Liste et calendrier des voitures"
            TabIndicatorProps={{ style: { display: 'none' } }}
          >
            <AntTab label="Liste des voitures" className={classes.tab} />
            <AntTab label="Calendrier des voitures" className={classes.tab} />
          </AntTabs>
        </Box>
      </Grid>

      {activeTab === 0 && (
        <Grid item xs={12}>
          <Paper className={classes.tablePaper} variant="outlined">
            <Box sx={{ height: 560, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.vhc_id}
                getRowHeight={() => 'auto'}
                getEstimatedRowHeight={() => 64}
                rowsPerPageOptions={[10, 20, 50]}
                pageSize={10}
                disableSelectionOnClick
                sx={{
                  border: 0,
                  color: '#334155',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f4f7fb',
                    borderBottom: '1px solid #dce6f3',
                    color: '#29476f',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                  '& .MuiDataGrid-row': { minHeight: '56px !important' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f8fbff' },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '56px !important',
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                    borderColor: '#edf1f7',
                    overflow: 'visible',
                  },
                  '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #dce6f3' },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid item xs={12}>
          <CalendarVehicle />
        </Grid>
      )}
    </Grid>
  );
};

export default VehicleManagmentTable;
